import React, { useEffect, useState } from "react";
import { Table, Checkbox, Button, Affix, message, Spin, Input } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ApiService from "../Components/apiService";
import { BASE_URL } from "../Components/Constant";

const AccessPolicyAppSettings = ({ selectedAccessPolicy, appsettingsData, accessPolicyData, onBack, onMain }) => {
    const [apps, setApps] = useState([]);
    const [checkedApps, setCheckedApps] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchApp, setSearchApp] = useState("");

    console.log('appsettingsData', appsettingsData);

  useEffect(() => {
    const fetchApps = async () => {
        try {
            const apiService = new ApiService(
                `${BASE_URL}/mt_apps`,
                { "Content-Type": "application/json" },
                "GET"
            );
            const response = await apiService.makeCall();
            if (Array.isArray(response)) {
                setApps(response.map((app) => ({ id: app._id, name: app.name })));

                const initialChecked = {};
                response.forEach((app) => {
                    const appName = app.name;
                    // Take from appsettingsData first, then accessPolicyData, otherwise default
                initialChecked[appName] = {
                        view: false,
                        create: false,
                        edit: false,
                        delete: false,
                        ...accessPolicyData?.app_settings?.[appName],
                        ...appsettingsData?.[appName],
                    };
                });

                setCheckedApps(initialChecked);
            }
        } catch (err) {
            message.error("Failed to fetch apps");
        }
    };

    fetchApps();
}, [accessPolicyData, appsettingsData]);

    // Toggle single app permission
    const handleCheckboxChange = (appId, action) => {
        if (!isEditing) return;
        setCheckedApps((prev) => {
            const newChecked = { ...prev };
            newChecked[appId][action] = !newChecked[appId][action];

            // dependent rules
            if (action === "edit" && newChecked[appId][action]) newChecked[appId].view = true;
            if (action === "create" && newChecked[appId][action]) {
                newChecked[appId].view = true;
                newChecked[appId].edit = true;
            }
            if (action === "delete" && newChecked[appId][action]) newChecked[appId].view = true;

            return newChecked;
        });
    };

    // Select all for an action
    const handleSelectAllChange = (action) => {
        if (!isEditing) return;
        const allSelected = apps.every((app) => checkedApps[app.name]?.[action]);
        const newChecked = { ...checkedApps };
        apps.forEach((app) => {
            newChecked[app.name][action] = !allSelected;
            if (!allSelected) {
                if (action === "create") {
                    newChecked[app.name].view = true;
                    newChecked[app.name].edit = true;
                }
                if (action === "edit") newChecked[app.name].view = true;
                if (action === "delete") newChecked[app.name].view = true;
            }
        });
        setCheckedApps(newChecked);
    };

    const saveAppPermissions = async () => {
        try {
            setLoading(true);
            accessPolicyData.app_settings = checkedApps;
            const apiService = new ApiService(
                `${BASE_URL}/update_access_policy/${selectedAccessPolicy}`,
                { "Content-Type": "application/json" },
                "PATCH",
                accessPolicyData
            );
            await apiService.makeCall();
            message.success("App permissions updated successfully");
            setIsEditing(false);
            onMain();
        } catch (err) {
            message.error("Error saving app permissions");
        } finally {
            setLoading(false);
        }
    };

    const cancelEditing = () => setIsEditing(false);

    const handleBack = () => {
        if (isEditing) {
            message.warning("Please save the settings before going back");
            return;
        }
        onBack();
    };

    const filteredApps = apps.filter((app) => app.name.toLowerCase().includes(searchApp.toLowerCase()));

    const columns = [
        {
            title: "App Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: (
                <div>
                    <Checkbox
                        checked={filteredApps.every((app) => checkedApps[app.name]?.view)}
                        onChange={() => handleSelectAllChange("view")}
                        disabled={!isEditing}
                    />
                    View
                </div>
            ),
            key: "view",
            render: (_, record) => (
                <Checkbox
                    checked={checkedApps[record.name]?.view}
                    onChange={() => handleCheckboxChange(record.name, "view")}
                    disabled={!isEditing}
                />
            ),
        },
        {
            title: (
                <div>
                    <Checkbox
                        checked={filteredApps.every((app) => checkedApps[app.name]?.create)}
                        onChange={() => handleSelectAllChange("create")}
                        disabled={!isEditing}
                    />
                    Create
                </div>
            ),
            key: "create",
            render: (_, record) => (
                <Checkbox
                    checked={checkedApps[record.name]?.create}
                    onChange={() => handleCheckboxChange(record.name, "create")}
                    disabled={!isEditing}
                />
            ),
        },
        {
            title: (
                <div>
                    <Checkbox
                        checked={filteredApps.every((app) => checkedApps[app.name]?.edit)}
                        onChange={() => handleSelectAllChange("edit")}
                        disabled={!isEditing}
                    />
                    Edit
                </div>
            ),
            key: "edit",
            render: (_, record) => (
                <Checkbox
                    checked={checkedApps[record.name]?.edit}
                    onChange={() => handleCheckboxChange(record.name, "edit")}
                    disabled={!isEditing}
                />
            ),
        },
        {
            title: (
                <div>
                    <Checkbox
                        checked={filteredApps.every((app) => checkedApps[app.name]?.delete)}
                        onChange={() => handleSelectAllChange("delete")}
                        disabled={!isEditing}
                    />
                    Delete
                </div>
            ),
            key: "delete",
            render: (_, record) => (
                <Checkbox
                    checked={checkedApps[record.name]?.delete}
                    onChange={() => handleCheckboxChange(record.name, "delete")}
                    disabled={!isEditing}
                />
            ),
        },
    ];

    return (
        <Spin spinning={loading}>
            <div style={{ padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <h2 style={{ margin: 0 }}>App Permissions</h2>
                    <div style={{ display: "flex", gap: 10 }}>
                        <Button type="default" onClick={handleBack}>
                            <ArrowLeftOutlined /> Back
                        </Button>
                        <Button type="primary" onClick={() => setIsEditing(!isEditing)}>
                            {isEditing ? "Cancel Edit" : "Edit"}
                        </Button>
                    </div>
                </div>

                <Input
                    placeholder="Search Apps"
                    value={searchApp}
                    onChange={(e) => setSearchApp(e.target.value)}
                    style={{ marginBottom: 10, width: 300 }}
                />

                <Table
                    columns={columns}
                    dataSource={filteredApps}
                    pagination={false}
                    rowKey="id"
                    style={{ marginBottom: 20, maxHeight: "70vh", overflowY: "auto" }}
                />

                {isEditing && (
                    <Affix offsetBottom={0}>
                        <div
                            style={{
                                position: "sticky",
                                bottom: 0,
                                background: "rgba(240, 242, 245, 0.95)",
                                padding: 10,
                                boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
                                textAlign: "center",
                            }}
                        >
                            <Button type="primary" onClick={saveAppPermissions} style={{ marginRight: 10 }}>
                                Save
                            </Button>
                            <Button type="default" onClick={cancelEditing}>
                                Cancel
                            </Button>
                        </div>
                    </Affix>
                )}
            </div>
        </Spin>
    );
};

export default AccessPolicyAppSettings;